import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { getFeatureFlags, PartsUrls, ServiceUrlsExceptionsForServiceDashboard, ServiceUrlsExceptions, SalesUrls, ServiceUrls, environment } from '../services/util.service';

@Injectable({
    providedIn: 'root'
})
export class ProtectRoute  {

    currentUrl = '';

    constructor(private readonly router: Router) {
    }

    canActivate(_route: ActivatedRouteSnapshot, state?: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
        this.currentUrl = this.getURL(state?.url || this.router.url);
        return this._isParentProtected();
    }

    canActivateChild(_childRoute: ActivatedRouteSnapshot, state?: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
        this.currentUrl = this.getURL(state?.url || this.router.url);
        return this._isChildProtected();
    }

    /* checks if the parent route can be accessed */
    private _isParentProtected() {
        if (this.currentUrl.includes('/bdc')) {
            return this.checkBDCAccess();
        } else if (this.currentUrl.includes('/parts')) {
            return environment.enableParts ? this.checkAccess('isPartsAccessible') : this.denyAccess();
        } else if (this.currentUrl.includes('/sales')) {
            return this.checkAccess('isSalesAccessibleFeatureFlag');
        } else if (this.currentUrl.includes('/service')) {
            return this.checkAccess('isServiceAccessibleFeatureFlag');
        } else if (this.currentUrl.includes('/deliveryChecklist')) {
            return this.checkAccess('isDeliveryChecklistAccessible');
        } else if (this.currentUrl.includes('/corporateDashboard')) {
            return environment.enableCorporateDashboard ? true : this.denyAccess();
        }
        return true;
    }

    /* checks if the child route can be accessed */
    private _isChildProtected() {
        if (PartsUrls.includes(this.currentUrl)) {
            return environment.enableParts ? this.checkAccess('isPartsAccessible') : this.denyAccess();
        } else if (SalesUrls.includes(this.currentUrl)) {
            return this.checkAccess('isSalesAccessibleFeatureFlag');
        } else if (ServiceUrls.includes(this.currentUrl)) {
            return this.checkAccess('isServiceAccessibleFeatureFlag');
        } else if (this.currentUrl.includes('/deliveryChecklist')) {
            return this.checkAccess('isDeliveryChecklistAccessible');
        }
        return true;
    }

    /* 
       Check if the flag is overriden in FMT
       If the overriden value in FMT is false, deny access else allow
       If the flag is not overriden, check DIS flags and allow access 
       accordingly.
    */
    private checkAccess(fmtFlag: string) {
        const fmtValue = getFeatureFlags()[fmtFlag];
        return this.checkFMTFlags(fmtFlag, fmtValue);
    }

    /* check if the bdc routs can be accessed based on sales and service flags */
    private checkBDCAccess() {
        const fmtSales = getFeatureFlags()['isSalesAccessibleFeatureFlag'];
        const fmtService = getFeatureFlags()['isServiceAccessibleFeatureFlag'];
        const overrideDashboardFlag = getFeatureFlags()['isSpServiceDashboardOverride'];
        const isDeliveryChecklistAccessible = getFeatureFlags()['isDeliveryChecklistAccessible'];
        if (fmtSales !== undefined && fmtService !== undefined) {
            if (!fmtSales && !fmtService) {
                if(overrideDashboardFlag && ServiceUrlsExceptionsForServiceDashboard.includes(this.currentUrl)){
                    return true 
                }
                if(!overrideDashboardFlag && isDeliveryChecklistAccessible){
                    if(["/asp/deliveryChecklist/summary"].includes(this.currentUrl)){
                        return true
                    }
                }
                return this.denyAccess();
            }
            return true;
        }
        return false;
    }

    private checkFMTFlags(fmtFlag, fmtValue) {
        if (!fmtValue) {
            if (fmtFlag === 'isServiceAccessibleFeatureFlag') {
                const overrideDashboardFlag = getFeatureFlags()['isSpServiceDashboardOverride'];
                if (overrideDashboardFlag && ServiceUrlsExceptions.includes(this.currentUrl)) {
                    return true;
                }
                return this.denyAccess();
            }
            return this.denyAccess();
        }
        return true;
    }

    private getURL(url?: string): string {
        if (!url) {
            return '';
        }
        return url.includes('?') ? url.slice(0, url.indexOf('?')) : url;
    }

    private denyAccess(): boolean | UrlTree {
        const featureFlags = getFeatureFlags();
        const isDeliveryChecklistAccessible = featureFlags['isDeliveryChecklistAccessible'];
        const isServiceAccessible = featureFlags['isServiceAccessibleFeatureFlag'];
        const isSalesAccessible = featureFlags['isSalesAccessibleFeatureFlag'];
        const isDashboardOverride = featureFlags['isSpServiceDashboardOverride'];

        // Redirect to checklist summary only when all service/sales/dashboard features are disabled
        if (
            isDeliveryChecklistAccessible &&
            this.currentUrl.includes('/asp') &&
            !isServiceAccessible &&
            !isSalesAccessible &&
            !isDashboardOverride
        ) {
            return this.router.parseUrl('/asp/deliveryChecklist/summary');
        }
        return this.router.parseUrl('/asp/not-authorized');
    }
}